import { z } from 'zod';
import { EpistemicStatus, LocationId, NpcTint, RelationshipStage, RelationshipType } from './enums';

export const EntityKind = z.enum(['npc', 'player', 'self']);
export type EntityKind = z.infer<typeof EntityKind>;

export const PersonSummary = z.object({
  entityId: z.string(),
  kind: EntityKind,
  name: z.string(),
  portraitUrl: z.string().nullable(),
  /** Full-body art (transparent) for page heroes and cast cards; null for other players. */
  figureUrl: z.string().nullable(),
  /** The character's own place (no people), behind their cast card and page; null until drawn. */
  backdropUrl: z.string().nullable().optional(),
  /** Card colour (a soft theme tint): the character's own for a neighbour. */
  tint: NpcTint,
  tagline: z.string(),
  stage: RelationshipStage,
  /** Affection shown as 0–5 hearts. */
  hearts: z.number().int().min(0).max(5),
  /** This character is the player's 看板娘 on Home (picked, or chosen for them as the closest). */
  isHomeCompanion: z.boolean(),
  types: z.array(RelationshipType),
  isRomanceable: z.boolean(),
  lastChapterTitle: z.string().nullable(),
  newSinceLastVisit: z.boolean(),
  /** It's their birthday today, on the player's own calendar. */
  birthdayToday: z.boolean(),
});
export type PersonSummary = z.infer<typeof PersonSummary>;

export const KnownFact = z.object({
  factId: z.string(),
  text: z.string(),
  status: EpistemicStatus,
  sourceLabel: z.string(),
  learnedOnDay: z.number().int(),
  isPreference: z.boolean(),
});
export type KnownFact = z.infer<typeof KnownFact>;

export const TimelineEntry = z.object({
  id: z.string(),
  gameDay: z.number().int(),
  title: z.string(),
  body: z.string(),
  chapterId: z.string().nullable(),
  kind: z.enum(['met', 'story', 'stage_change', 'message', 'shared']),
});
export type TimelineEntry = z.infer<typeof TimelineEntry>;

/**
 * 人物檔案: the facts about a neighbour the player learns as they get closer. The birthday and
 * star sign are shown from the start, unless a story tells the birthday (then null, with
 * `birthdayHint`, until it's played); each entry is either open (its text) or locked (??? and what opens it).
 */
export const PersonProfile = z.object({
  birthday: z.object({ month: z.number().int(), day: z.number().int(), label: z.string() }).nullable(),
  zodiac: z.string().nullable(),
  /** Why the birthday is still ???, while it is. */
  birthdayHint: z.string().nullable(),
  birthdayToday: z.boolean(),
  /** How much of the dossier is open, 0–100. */
  completion: z.number().int().min(0).max(100),
  entries: z.array(
    z.object({
      field: z.string(),
      label: z.string(),
      /** Null while locked: nothing hidden is sent. */
      text: z.string().nullable(),
      locked: z.boolean(),
      /** What opens it, while locked. */
      hint: z.string().nullable(),
    }),
  ),
});
export type PersonProfile = z.infer<typeof PersonProfile>;

export const PersonDetail = PersonSummary.extend({
  bio: z.string(),
  age: z.number().int().nullable(),
  /** Words, not numbers: NPC feelings are shown as descriptors plus evidence (plan §9). */
  feelings: z.array(z.string()),
  knownFacts: z.array(KnownFact),
  timeline: z.array(TimelineEntry),
  openThreads: z.array(z.object({ templateKey: z.string(), title: z.string(), hint: z.string() })),
  canMessage: z.boolean(),
  conversationId: z.string().nullable(),
  /** Null for a real friend, or a neighbour whose dossier isn't written yet. */
  profile: PersonProfile.nullable(),
});
export type PersonDetail = z.infer<typeof PersonDetail>;

export const GraphNode = z.object({
  id: z.string(),
  kind: EntityKind,
  name: z.string(),
  portraitUrl: z.string().nullable(),
  stage: RelationshipStage.nullable(),
  isAnonymous: z.boolean(),
});
export const GraphEdge = z.object({
  id: z.string(),
  from: z.string(),
  to: z.string(),
  types: z.array(RelationshipType),
  label: z.string(),
  mutual: z.boolean(),
  status: EpistemicStatus,
  sourceLabel: z.string(),
  hasTension: z.boolean(),
});
export const RelationshipGraph = z.object({
  centerId: z.string(),
  depth: z.number().int(),
  nodes: z.array(GraphNode),
  edges: z.array(GraphEdge),
  truncated: z.boolean(),
});
export type GraphNode = z.infer<typeof GraphNode>;
export type GraphEdge = z.infer<typeof GraphEdge>;
export type RelationshipGraph = z.infer<typeof RelationshipGraph>;

export const GraphQuery = z.object({
  centerId: z.string().max(64).optional(),
  depth: z.coerce.number().int().min(1).max(2).default(1),
  filter: z.enum(['all', 'friends', 'romance', 'npc', 'players']).default('all'),
});
export type GraphQuery = z.infer<typeof GraphQuery>;
export type GraphFilter = GraphQuery['filter'];

// ---------- gifts and invitations ----------

export const GiftView = z.object({
  id: z.string(),
  name: z.string(),
  coinPrice: z.number().int(),
  icon: z.string(),
  /** What you've found out by giving it before: they loved it / liked it. Null until then. */
  known: z.enum(['loved', 'liked']).nullable(),
});
export type GiftView = z.infer<typeof GiftView>;
export const PersonActions = z.object({
  gifts: z.array(GiftView),
  giftedToday: z.boolean(),
  coins: z.number().int(),
  invite: z.object({ available: z.boolean(), reason: z.string().nullable() }),
});
export type PersonActions = z.infer<typeof PersonActions>;
export const GiveGiftRequest = z.object({ giftId: z.string().max(40) });
export const GiftResult = z.object({ reaction: z.array(z.string()), tier: z.enum(['loved', 'liked', 'nice']), hearts: z.number().int().min(0).max(5) });
export type GiftResult = z.infer<typeof GiftResult>;
export const InviteRequest = z.object({ locationId: LocationId });
export const InviteResult = z.object({ accepted: z.boolean(), message: z.string() });
export type InviteResult = z.infer<typeof InviteResult>;
